import { API_CONFIG } from '@Web/config/api.config';
import { RestClient } from '@Web/clients/RestClient';
import {
  resolveAccessToken,
  waitForAccessToken,
} from '@core/utils/resolveAccessToken';
import { AuthService } from '@core/Web/services/AuthService';
import { useAuth } from '@core/gameengine/state/auth';

export interface SystemAvatar {
  id: string;
  /** Local manifest key, e.g. avatar3 → images/avatars/avatar3.png */
  avatarKey: string;
  label?: string;
}

/** Avatar from GET /api/player/v1/avatars (CDN image). */
export interface PlayerAvatar {
  id: string;
  /** Fully resolved image URL for <img src>. */
  imageUrl: string;
  label?: string;
  selected?: boolean;
  /** Optional local manifest key when the API maps to avatar1…avatar15. */
  avatarKey?: string;
  /** API id sent to POST /api/player/v1/avatars/select (never a local avatarKey). */
  serverAvatarId?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LOCAL_AVATAR_KEY_RE = /^avatar([1-9]|1[0-5])$/i;

export function isUuid(value: string | undefined | null): boolean {
  return Boolean(value && UUID_RE.test(value));
}

/** Local manifest keys / UI placeholders — not valid for POST …/avatars/select. */
export function isLocalAvatarKey(value: string | undefined | null): boolean {
  const v = value?.trim();
  if (!v) return false;
  return LOCAL_AVATAR_KEY_RE.test(v) || /^avatar-\d+$/i.test(v);
}

/** Player id for avatar select API (session / profile). */
export function resolvePlayerIdForAvatarApi(): string | null {
  return (
    AuthService.getInstance().getPlayerId() ??
    useAuth.getState().user?.playerId ??
    null
  );
}

/** Id to pass to POST /api/player/v1/avatars/select (from GET /api/player/v1/avatars). */
export function getServerAvatarIdForSelect(
  avatar: PlayerAvatar
): string | null {
  const candidates = [avatar.serverAvatarId, avatar.id];
  for (const raw of candidates) {
    const id = raw?.trim();
    if (id && !isLocalAvatarKey(id)) return id;
  }
  return null;
}

const PLAYER_AVATAR_CDN_BASE =
  import.meta.env.VITE_PLAYER_AVATAR_CDN_BASE?.replace(/\/$/, '') ??
  'https://be.play-studios.ai/sites/websites/avatars';

type RawAvatar = Record<string, unknown>;

function pickString(obj: RawAvatar, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
}

function pickNumber(obj: RawAvatar, keys: string[]): number | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** Map API / file hints to avatar1 … avatar15 keys used under public/games/skyx_new. */
export function resolveAvatarKeyFromRecord(
  item: RawAvatar,
  index = 0
): string | null {
  const candidates = [
    pickString(item, [
      'avatarCode',
      'code',
      'name',
      'fileName',
      'imageName',
      'imagePath',
      'path',
      'url',
      'imageUrl',
      'avatarUrl',
    ]),
    (() => {
      const order =
        pickNumber(item, ['sortOrder', 'order', 'index', 'position']) ??
        pickNumber(item, ['avatarNumber', 'number']);
      return order != null && order >= 1 && order <= 15
        ? `avatar${order}`
        : undefined;
    })(),
    typeof item.id === 'string' || typeof item.id === 'number'
      ? String(item.id)
      : undefined,
  ].filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    const match =
      candidate.match(/avatar\s*_?\s*(\d{1,2})/i) ??
      candidate.match(/(\d{1,2})\.png$/i);
    if (match) {
      const n = Number(match[1]);
      if (n >= 1 && n <= 15) return `avatar${n}`;
    }
    if (/^avatar\d{1,2}$/i.test(candidate)) {
      const n = Number(candidate.replace(/\D/g, ''));
      if (n >= 1 && n <= 15) return `avatar${n}`;
    }
  }

  const fallbackIndex = index + 1;
  if (fallbackIndex >= 1 && fallbackIndex <= 15) {
    return `avatar${fallbackIndex}`;
  }
  return null;
}

function extractAvatarsPayload(data: unknown): RawAvatar[] {
  if (Array.isArray(data)) return data as RawAvatar[];
  if (!data || typeof data !== 'object') return [];

  const obj = data as Record<string, unknown>;
  const candidates = [
    obj.available,
    obj.content,
    obj.items,
    obj.data,
    obj.avatars,
    (obj.data as Record<string, unknown> | undefined)?.content,
    (obj.data as Record<string, unknown> | undefined)?.items,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as RawAvatar[];
  }

  return [];
}

/**
 * GET /api/player/v1/avatars returns `{ selected, available, pagination }`.
 * Merge selected (may be absent from available) with the available list.
 */
function extractPlayerAvatarsPayload(data: unknown): RawAvatar[] {
  if (!data || typeof data !== 'object') {
    return extractAvatarsPayload(data);
  }

  const obj = data as Record<string, unknown>;
  const hasPlayerShape =
    obj.available != null ||
    (obj.selected != null && typeof obj.selected === 'object');

  if (!hasPlayerShape) {
    return extractAvatarsPayload(data);
  }

  const rows: RawAvatar[] = [];
  const seenIds = new Set<string>();

  const pushRecord = (item: RawAvatar, selected = false) => {
    const id = pickString(item, ['id', 'avatarId', 'uuid']);
    if (id && seenIds.has(id)) return;
    if (id) seenIds.add(id);
    rows.push(selected ? { ...item, selected: true, isSelected: true } : item);
  };

  const selected = obj.selected;
  if (selected && typeof selected === 'object' && !Array.isArray(selected)) {
    pushRecord(selected as RawAvatar, true);
  }

  if (Array.isArray(obj.available)) {
    for (const item of obj.available) {
      if (item && typeof item === 'object') {
        pushRecord(item as RawAvatar, false);
      }
    }
  }

  if (rows.length > 0) return rows;

  return extractAvatarsPayload(data);
}

function avatarSortKey(avatar: PlayerAvatar): number {
  const fromKey = avatar.avatarKey?.match(/\d+/)?.[0];
  if (fromKey) return Number(fromKey);
  const fromLabel = avatar.label?.match(/\d+/)?.[0];
  if (fromLabel) return Number(fromLabel);
  return Number.MAX_SAFE_INTEGER;
}

function isSelectedAvatarRecord(item: RawAvatar): boolean {
  return (
    item.selected === true ||
    item.isSelected === true ||
    item.active === true ||
    item.current === true ||
    item.isCurrent === true
  );
}

/** Build CDN URL: …/avatars/common/{imageUrl} */
export function buildPlayerAvatarCdnUrl(imageRef: string): string {
  const ref = imageRef.trim();
  if (!ref) return '';
  if (/^https?:\/\//i.test(ref)) return ref;

  const clean = ref.replace(/^\//, '');
  const base = PLAYER_AVATAR_CDN_BASE.replace(/\/$/, '');
  if (clean.startsWith('common/') || clean.includes('/')) {
    return `${base}/${clean}`;
  }
  return `${base}/common/${clean}`;
}

function normalizePlayerAvatar(
  item: RawAvatar,
  index: number
): PlayerAvatar | null {
  const imageRef = pickString(item, [
    'imageUrl',
    'imagePath',
    'url',
    'avatarUrl',
    'fileName',
    'imageName',
    'path',
  ]);
  if (!imageRef) return null;

  const rawServerId =
    pickString(item, ['avatarId', 'id', 'uuid', 'avatarUUID']) ?? undefined;
  const serverAvatarId =
    rawServerId && !isLocalAvatarKey(rawServerId) ? rawServerId : undefined;
  const avatarKey = resolveAvatarKeyFromRecord(item, index) ?? undefined;
  const id =
    serverAvatarId ??
    pickString(item, ['code', 'avatarCode']) ??
    avatarKey ??
    `avatar-${index}`;

  return {
    id,
    serverAvatarId,
    imageUrl: buildPlayerAvatarCdnUrl(imageRef),
    label: pickString(item, ['label', 'displayName', 'title', 'name']),
    selected: isSelectedAvatarRecord(item),
    avatarKey: avatarKey ?? undefined,
  };
}

function pickSelectedAvatarIdFromPayload(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const obj = data as Record<string, unknown>;

  const selected = obj.selected;
  if (selected && typeof selected === 'object' && !Array.isArray(selected)) {
    const id = pickString(selected as RawAvatar, ['id', 'avatarId', 'uuid']);
    if (id) return id;
  }

  return pickString(obj, [
    'selectedAvatarId',
    'avatarId',
    'currentAvatarId',
    'selectedId',
  ]);
}

function normalizeAvatar(item: RawAvatar, index: number): SystemAvatar | null {
  const avatarKey = resolveAvatarKeyFromRecord(item, index);
  if (!avatarKey) return null;

  const id =
    pickString(item, ['id', 'avatarId', 'uuid']) ??
    pickString(item, ['code', 'avatarCode']) ??
    avatarKey;

  const label =
    pickString(item, ['label', 'displayName', 'title', 'name']) ?? undefined;

  return { id, avatarKey, label };
}

const PLAYER_AVATARS_CACHE_TTL_MS = 60_000;

export class AvatarService {
  private static instance: AvatarService;
  private restClient: RestClient;
  private playerAvatarsCache: PlayerAvatar[] | null = null;
  private playerAvatarsCacheAt = 0;
  private playerAvatarsInflight: Promise<PlayerAvatar[]> | null = null;

  public static getInstance(): AvatarService {
    if (!AvatarService.instance) {
      AvatarService.instance = new AvatarService();
    }
    return AvatarService.instance;
  }

  private constructor() {
    this.restClient = new RestClient(API_CONFIG.baseUrl);
  }

  public async fetchSystemAvatars(
    page = 0,
    size = 100
  ): Promise<SystemAvatar[]> {
    const token = resolveAccessToken();
    if (!token) {
      throw new Error('Cannot load avatars without an access token.');
    }

    const path = API_CONFIG.endpoints.systemAvatars(page, size);
    const data = await this.restClient.get<unknown>(path, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    const seen = new Set<string>();
    const avatars: SystemAvatar[] = [];

    for (const [index, item] of extractAvatarsPayload(data).entries()) {
      const normalized = normalizeAvatar(item, index);
      if (!normalized || seen.has(normalized.avatarKey)) continue;
      seen.add(normalized.avatarKey);
      avatars.push(normalized);
    }

    return avatars;
  }

  public invalidatePlayerAvatarsCache(): void {
    this.playerAvatarsCache = null;
    this.playerAvatarsCacheAt = 0;
    this.playerAvatarsInflight = null;
  }

  /** Player avatars from GET /api/player/v1/avatars (cached + deduped). */
  public async fetchPlayerAvatars(options?: {
    force?: boolean;
  }): Promise<PlayerAvatar[]> {
    const force = options?.force ?? false;
    const now = Date.now();

    if (
      !force &&
      this.playerAvatarsCache &&
      now - this.playerAvatarsCacheAt < PLAYER_AVATARS_CACHE_TTL_MS
    ) {
      return this.playerAvatarsCache;
    }

    if (this.playerAvatarsInflight) {
      return this.playerAvatarsInflight;
    }

    this.playerAvatarsInflight = this.fetchPlayerAvatarsFromNetwork()
      .then(avatars => {
        this.playerAvatarsCache = avatars;
        this.playerAvatarsCacheAt = Date.now();
        return avatars;
      })
      .finally(() => {
        this.playerAvatarsInflight = null;
      });

    return this.playerAvatarsInflight;
  }

  private async fetchPlayerAvatarsFromNetwork(): Promise<PlayerAvatar[]> {
    const token = resolveAccessToken();
    if (!token) {
      throw new Error('Cannot load player avatars without an access token.');
    }

    const data = await this.restClient.get<unknown>(
      API_CONFIG.endpoints.playerAvatars,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      }
    );

    const selectedId = pickSelectedAvatarIdFromPayload(data);
    const seen = new Set<string>();
    const avatars: PlayerAvatar[] = [];

    for (const [index, item] of extractPlayerAvatarsPayload(data).entries()) {
      const normalized = normalizePlayerAvatar(item, index);
      if (!normalized) continue;

      const dedupeKey = normalized.serverAvatarId ?? normalized.id;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      if (
        selectedId &&
        (normalized.serverAvatarId === selectedId ||
          normalized.id === selectedId)
      ) {
        normalized.selected = true;
      }

      avatars.push(normalized);
    }

    avatars.sort((a, b) => avatarSortKey(a) - avatarSortKey(b));

    return avatars;
  }

  /** Currently selected player avatar, if any. */
  public async fetchSelectedPlayerAvatar(): Promise<PlayerAvatar | null> {
    const avatars = await this.fetchPlayerAvatars();
    return avatars.find(a => a.selected) ?? null;
  }

  /** POST /api/player/v1/avatars/select */
  public async selectPlayerAvatar(avatarId: string): Promise<void> {
    const token = await waitForAccessToken();
    if (!token) {
      throw new Error('Cannot select avatar without an access token.');
    }
    if (!avatarId?.trim()) {
      throw new Error('avatarId is required to select an avatar.');
    }
    if (isLocalAvatarKey(avatarId)) {
      throw new Error('Cannot select a local-only avatar id on the server.');
    }

    const body: Record<string, string> = { avatarId: avatarId.trim() };
    const playerId = resolvePlayerIdForAvatarApi();
    if (playerId) {
      body.playerId = playerId;
    }

    await this.restClient.post(API_CONFIG.endpoints.playerAvatarSelect, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.invalidatePlayerAvatarsCache();
  }

  /** Returns cached list when still fresh (no network). */
  public peekPlayerAvatarsCache(): PlayerAvatar[] | null {
    if (!this.playerAvatarsCache) return null;
    if (Date.now() - this.playerAvatarsCacheAt >= PLAYER_AVATARS_CACHE_TTL_MS) {
      return null;
    }
    return this.playerAvatarsCache;
  }

  /**
   * URL for the menu: server selection → local CDN cache → empty while loading.
   */
  public async resolveMenuPlayerAvatarUrl(
    readLocal?: () => {
      imageUrl?: string;
      avatarKey?: string;
      avatarId?: string;
    } | null
  ): Promise<string> {
    try {
      const selected = await this.fetchSelectedPlayerAvatar();
      if (selected?.imageUrl) return selected.imageUrl;
    } catch (error) {
      console.warn(
        '[AvatarService] Failed to load selected player avatar',
        error
      );
    }

    const local = readLocal?.();
    if (local?.imageUrl && /^https?:\/\//i.test(local.imageUrl)) {
      return local.imageUrl;
    }

    if (local?.avatarId || local?.avatarKey) {
      try {
        const avatars = await this.fetchPlayerAvatars();
        if (local.avatarId) {
          const byId = avatars.find(entry => {
            const serverId = getServerAvatarIdForSelect(entry);
            return (
              serverId === local.avatarId ||
              entry.id === local.avatarId ||
              entry.serverAvatarId === local.avatarId
            );
          });
          if (byId?.imageUrl) return byId.imageUrl;
        }
        if (local.avatarKey) {
          const byKey = avatars.find(
            entry => entry.avatarKey === local.avatarKey
          );
          if (byKey?.imageUrl) return byKey.imageUrl;
        }
      } catch (error) {
        console.warn(
          '[AvatarService] Failed to resolve stored player avatar',
          error
        );
      }
    }

    return '';
  }
}
